/**
 * Public Parent Consent Page — Deskey DPDP Act 2023 (Verifiable Parental Consent)
 *
 * Route: /consent/:token        (no auth)
 *
 * Parent flow:
 *   1. Read agreement
 *   2. Verify identity via Aadhaar OTP / DigiLocker / Offline upload
 *   3. Confirm with the agreed checkbox + tap "I consent"
 *   4. Student account is auto-created on the backend
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ShieldCheck, Fingerprint, FileText, Upload, AlertTriangle, CheckCircle2, Loader2, XCircle, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { notifyError, friendlyError } from '../lib/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#34A853';
const DARK = '#0C1A12';
const SOFT = '#F0FDF4';

const STATUS_BADGE = {
  pending:   { color: '#0EA5E9', label: 'Awaiting Verification' },
  verified:  { color: '#F59E0B', label: 'Identity Verified — Awaiting Approval' },
  approved:  { color: GREEN,     label: 'Consent Granted' },
  rejected:  { color: '#DC2626', label: 'Declined' },
  expired:   { color: '#6B7280', label: 'Expired' },
  revoked:   { color: '#6B7280', label: 'Revoked' },
};

const TABS = [
  { id: 'aadhaar',    label: 'Aadhaar OTP',    icon: Fingerprint, sub: 'eKYC via UIDAI' },
  { id: 'digilocker', label: 'DigiLocker',     icon: ShieldCheck, sub: 'Government wallet' },
  { id: 'offline',    label: 'Offline Letter', icon: FileText,    sub: 'Upload signed PDF' },
];

export default function PublicConsentPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('aadhaar');

  // Aadhaar state
  const [aadhaar, setAadhaar] = useState('');
  const [aadhaarSent, setAadhaarSent] = useState(false);
  const [aadhaarOtp, setAadhaarOtp] = useState('');
  const [aadhaarHint, setAadhaarHint] = useState(null);
  const [aadhaarBusy, setAadhaarBusy] = useState(false);

  // Digilocker
  const [dlBusy, setDlBusy] = useState(false);
  const [dlState, setDlState] = useState(null);

  // Offline
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [offlineBusy, setOfflineBusy] = useState(false);

  // Final consent
  const [agreed, setAgreed] = useState(false);
  const [approving, setApproving] = useState(false);
  const [final, setFinal] = useState(null);          // { ok, message, default_password }

  const reload = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${API}/public/consent/${token}`);
      setConsent(data);
    } catch (e) {
      setError(friendlyError(e, 'Unable to load consent request'));
    }
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [token]);

  const verified = consent?.verification?.method;
  const approved = consent?.status === 'approved';

  // ── Aadhaar handlers ───────────────────────────────────────────────────────
  const sendAadhaarOtp = async () => {
    setAadhaarBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/consent/${token}/aadhaar/send-otp`, { aadhaar });
      setAadhaarSent(true);
      setAadhaarHint(data.mock_otp_hint);
      toast.success(`OTP sent to your Aadhaar-linked mobile (${data.masked_aadhaar})`);
    } catch (e) {
      notifyError(e, 'OTP send failed');
    }
    setAadhaarBusy(false);
  };
  const verifyAadhaarOtp = async () => {
    setAadhaarBusy(true);
    try {
      await axios.post(`${API}/public/consent/${token}/aadhaar/verify`, { otp: aadhaarOtp });
      toast.success('Aadhaar verified');
      await reload();
    } catch (e) {
      notifyError(e, 'OTP verification failed');
    }
    setAadhaarBusy(false);
  };

  // ── DigiLocker ─────────────────────────────────────────────────────────────
  const startDigilocker = async () => {
    setDlBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/consent/${token}/digilocker/start`, {});
      setDlState(data);
      toast.info(data.mocked ? 'DigiLocker (MOCK) — tap below to simulate approval' : 'Redirecting…');
    } catch (e) {
      notifyError(e, 'DigiLocker failed to start');
    }
    setDlBusy(false);
  };
  const completeDigilocker = async () => {
    if (!dlState?.state) return;
    setDlBusy(true);
    try {
      await axios.post(`${API}/public/consent/${token}/digilocker/complete`, { state: dlState.state });
      toast.success('DigiLocker verified');
      await reload();
    } catch (e) {
      notifyError(e, 'DigiLocker verification failed');
    }
    setDlBusy(false);
  };

  // ── Offline upload ─────────────────────────────────────────────────────────
  const uploadOffline = async () => {
    if (!file) { toast.error('Pick a PDF/JPG file first'); return; }
    setOfflineBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    if (note) fd.append('note', note);
    try {
      await axios.post(`${API}/public/consent/${token}/offline-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Signed letter uploaded — identity marked verified');
      await reload();
    } catch (e) {
      notifyError(e, 'Upload failed');
    }
    setOfflineBusy(false);
  };

  // ── Final approval ─────────────────────────────────────────────────────────
  const approve = async () => {
    if (!verified) { toast.error('Verify your identity first'); return; }
    if (!agreed) { toast.error('Please tick the agreement box'); return; }
    setApproving(true);
    try {
      const { data } = await axios.post(`${API}/public/consent/${token}/approve`, {
        agreement_version: consent.agreement_version, agreed: true,
      });
      setFinal(data);
      toast.success('Consent recorded — student account activated');
    } catch (e) {
      notifyError(e, 'Approval failed');
    }
    setApproving(false);
  };

  const reject = async () => {
    const reason = window.prompt('Reason (optional)') || null;
    try {
      await axios.post(`${API}/public/consent/${token}/reject`, { reason });
      toast.success('Consent declined');
      reload();
    } catch (e) {
      notifyError(e, 'Failed to reject');
    }
  };

  if (loading) return <CenterLoader label="Loading parental consent…" />;
  if (error)   return <ErrorCard message={error} />;
  if (!consent) return null;

  const badge = STATUS_BADGE[consent.status] || STATUS_BADGE.pending;
  const verifiedBadgeColor = verified ? GREEN : '#94A3B8';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: SOFT }} data-testid="public-consent-page">
      {/* Top brand strip */}
      <div className="w-full px-4 py-3 border-b border-emerald-100 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: GREEN }}>D</div>
            <div>
              <div className="text-sm font-bold" style={{ color: DARK, fontFamily: 'Space Grotesk, sans-serif' }}>Deskey</div>
              <div className="text-[10px] text-emerald-700 -mt-0.5 tracking-wider uppercase">DPDP Verifiable Parental Consent</div>
            </div>
          </div>
          <span className="text-[11px] font-bold px-3 py-1.5 rounded-full text-white" style={{ background: badge.color }} data-testid="consent-status-badge">
            {badge.label}
          </span>
        </div>
      </div>

      <div className="flex-1 max-w-5xl w-full mx-auto px-4 py-6 sm:py-10">
        {/* Already-approved success card */}
        {(approved || final) && (
          <div className="rounded-2xl bg-white border-2 border-emerald-200 p-7 shadow-lg" data-testid="consent-approved-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: SOFT }}>
                <CheckCircle2 className="w-6 h-6" style={{ color: GREEN }} />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold" style={{ color: DARK, fontFamily: 'Space Grotesk, sans-serif' }}>
                  Consent Recorded — {consent.student.full_name} is now active on Deskey
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  We've created an account for your child at <strong>{consent.institution_name}</strong>. They'll receive their login details by email.
                </p>
                {final?.default_password && (
                  <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-xs">
                    <strong>Default password:</strong>{' '}
                    <code className="font-mono bg-white px-1.5 py-0.5 rounded border border-emerald-100">{final.default_password}</code>{' '}
                    <span className="text-slate-500">— ask your child to change it on first login.</span>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-3">
                  <strong>Future purchases:</strong> any Super-Credit purchase from this account will require your live OTP approval — even if you signed offline. This is your DPDP-mandated right as a guardian.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Live flow */}
        {!approved && !final && (
          <>
            {/* Intro card */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="consent-intro-card">
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: DARK, fontFamily: 'Space Grotesk, sans-serif' }}>
                Approve <span style={{ color: GREEN }}>{consent.student.full_name}'s</span> Deskey account
              </h1>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Hi <strong>{consent.parent.full_name}</strong>, <strong>{consent.institution_name}</strong> has invited your child to Deskey. India's
                <strong> Digital Personal Data Protection Act, 2023 (Section 9)</strong> requires us to obtain your verifiable consent before activating any
                account for a child under 18.
              </p>

              <div className="mt-4 grid sm:grid-cols-3 gap-2.5 text-xs">
                <FactRow label="Student" value={`${consent.student.full_name} · Age ${consent.student.age}`} />
                <FactRow label="Institution" value={consent.institution_name} />
                <FactRow label="Your Relationship" value={consent.parent.relationship} />
              </div>
            </div>

            {/* Agreement card */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 mt-5" data-testid="consent-agreement-card">
              <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Agreement <span className="text-[10px] font-medium text-slate-500 ml-2">Version {consent.agreement_version}</span>
              </h2>
              <div className="text-[13px] text-slate-700 space-y-3 leading-relaxed border border-slate-100 rounded-xl p-4 bg-slate-50/60 max-h-72 overflow-y-auto" data-testid="dpdp-legal-text">
                <p>By approving, I confirm that:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>I am the <strong>lawful parent or legal guardian</strong> of {consent.student.full_name}, currently {consent.student.age} years old.</li>
                  <li>I authorise <strong>Deskey</strong> and <strong>{consent.institution_name}</strong> to create an account for my child, process
                    personal data strictly for educational purposes, and store it within India in compliance with the <strong>DPDP Act 2023</strong>.</li>
                  <li>I understand my rights under DPDP §11–14 — to access, correct, erase, and withdraw consent at any time by writing to
                    <strong> dpo@deskey.app</strong>.</li>
                  <li>I confirm my contact details (email & mobile) on file are correct; <strong>all future Super-Credit purchases by my child will require my live OTP approval</strong>, and only I will be the payer.</li>
                  <li>Deskey will not engage in <strong>behavioural advertising, profiling, or tracking</strong> of my child for commercial purposes (per DPDP §10).</li>
                  <li>I have read and accept Deskey's <a className="underline text-emerald-700" href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and <a className="underline text-emerald-700" href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>.</li>
                </ol>
                <p className="text-[11px] text-slate-500 pt-2 border-t border-slate-200">
                  This consent record (including your IP, device, timestamp, and verification method) is stored as an
                  immutable audit log — your "verifiable" consent under the DPDP Act.
                </p>
              </div>
            </div>

            {/* Verification tabs */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm mt-5 overflow-hidden" data-testid="consent-verify-card">
              <div className="border-b border-slate-100 px-6 pt-5 pb-3 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Verify your identity</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Pick any one method — your choice is recorded.</p>
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: verified ? '#DCFCE7' : '#F1F5F9', color: verifiedBadgeColor }}>
                  {verified ? '✓ Verified' : 'Pending'}
                </span>
              </div>

              {/* Tab buttons */}
              <div className="grid grid-cols-3 border-b border-slate-100">
                {TABS.map((t) => (
                  <button key={t.id}
                    onClick={() => setTab(t.id)}
                    data-testid={`consent-tab-${t.id}`}
                    disabled={!!verified}
                    className={`px-4 py-3 text-left transition-colors border-b-2 ${
                      tab === t.id ? 'border-emerald-500 bg-emerald-50/40' : 'border-transparent hover:bg-slate-50'
                    } ${verified && verified !== ({aadhaar:'aadhaar_otp', digilocker:'digilocker', offline:'offline_signed_letter'}[t.id]) ? 'opacity-50' : ''}`}>
                    <div className="flex items-start gap-2.5">
                      <t.icon className="w-4 h-4 mt-0.5" style={{ color: tab === t.id ? GREEN : '#64748B' }} />
                      <div>
                        <div className="text-[13px] font-semibold text-slate-800">{t.label}</div>
                        <div className="text-[11px] text-slate-500 -mt-0.5">{t.sub}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="p-6">
                {tab === 'aadhaar' && (
                  <div data-testid="tab-content-aadhaar">
                    <MockedBanner active={consent.kyc_providers.aadhaar_mocked} label="Aadhaar eKYC (MOCKED)" hint="Use OTP 123456 to simulate success." />
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Parent's Aadhaar Number</label>
                    <Input type="text" inputMode="numeric" maxLength={14} placeholder="1234 5678 9012"
                           value={aadhaar} onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, '').slice(0, 12))}
                           disabled={aadhaarSent} data-testid="aadhaar-input"
                           className="font-mono h-11 rounded-lg border-slate-200" />
                    {!aadhaarSent ? (
                      <Button onClick={sendAadhaarOtp} disabled={aadhaarBusy || aadhaar.length !== 12}
                              data-testid="aadhaar-send-otp"
                              className="mt-3 h-10 px-5 text-sm font-semibold text-white rounded-lg"
                              style={{ background: GREEN }}>
                        {aadhaarBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Fingerprint className="w-4 h-4 mr-2"/>}
                        Send OTP
                      </Button>
                    ) : (
                      <div className="mt-4 space-y-3">
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Enter 6-digit OTP</label>
                        <div className="flex gap-2">
                          <Input type="text" inputMode="numeric" maxLength={6} placeholder="••••••"
                                 value={aadhaarOtp} onChange={(e) => setAadhaarOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                 data-testid="aadhaar-otp-input"
                                 className="font-mono text-center text-lg tracking-[0.5em] h-12 rounded-lg border-slate-200 max-w-[200px]" />
                          <Button onClick={verifyAadhaarOtp} disabled={aadhaarBusy || aadhaarOtp.length !== 6}
                                  data-testid="aadhaar-verify-otp"
                                  className="h-12 px-5 text-sm font-semibold text-white rounded-lg"
                                  style={{ background: GREEN }}>
                            {aadhaarBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : null}
                            Verify
                          </Button>
                        </div>
                        {aadhaarHint && (
                          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 inline-flex items-center gap-1.5">
                            <AlertTriangle className="w-3 h-3"/> Mock OTP: <code className="font-mono">{aadhaarHint}</code>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {tab === 'digilocker' && (
                  <div data-testid="tab-content-digilocker">
                    <MockedBanner active={consent.kyc_providers.digilocker_mocked} label="DigiLocker (MOCKED)" hint="Tap the buttons to simulate a successful OAuth handshake." />
                    {!dlState ? (
                      <Button onClick={startDigilocker} disabled={dlBusy}
                              data-testid="digilocker-start"
                              className="h-11 px-5 text-sm font-semibold text-white rounded-lg"
                              style={{ background: GREEN }}>
                        {dlBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <ShieldCheck className="w-4 h-4 mr-2"/>}
                        Open DigiLocker
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <div className="text-[13px] text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4">
                          <p className="font-semibold mb-1">DigiLocker authorisation request</p>
                          <p className="text-xs text-slate-600">Deskey is requesting verification of your Aadhaar card stored in DigiLocker. State: <code className="text-[10px] font-mono">{dlState.state.slice(0, 18)}…</code></p>
                        </div>
                        <Button onClick={completeDigilocker} disabled={dlBusy}
                                data-testid="digilocker-complete"
                                className="h-11 px-5 text-sm font-semibold text-white rounded-lg"
                                style={{ background: GREEN }}>
                          {dlBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <CheckCircle2 className="w-4 h-4 mr-2"/>}
                          I authorise — share my Aadhaar
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {tab === 'offline' && (
                  <div data-testid="tab-content-offline">
                    <div className="text-[13px] text-slate-600 mb-4 leading-relaxed">
                      Download and print our consent letter, sign it physically, then upload a scan/photo here. Acceptable: PDF / JPG / PNG, max 5 MB.
                    </div>
                    <a className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 hover:underline mb-3" href="/legal/dpdp-consent-template.pdf" target="_blank" rel="noreferrer">
                      <FileText className="w-3.5 h-3.5"/> Download consent letter template
                    </a>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Signed letter</label>
                    <input type="file" accept="application/pdf,image/png,image/jpeg,image/jpg"
                           onChange={(e) => setFile(e.target.files?.[0])}
                           data-testid="offline-file-input"
                           className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100" />
                    {file && <p className="text-[11px] text-slate-500 mt-1.5">Selected: {file.name} ({Math.round(file.size/1024)} KB)</p>}

                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mt-4 mb-1.5">Note (optional)</label>
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Signed before class teacher" data-testid="offline-note-input" className="h-10 rounded-lg border-slate-200" />

                    <Button onClick={uploadOffline} disabled={offlineBusy || !file}
                            data-testid="offline-upload-btn"
                            className="mt-4 h-11 px-5 text-sm font-semibold text-white rounded-lg"
                            style={{ background: GREEN }}>
                      {offlineBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Upload className="w-4 h-4 mr-2"/>}
                      Upload signed letter
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Final consent */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm mt-5 p-6 sm:p-8" data-testid="consent-final-card">
              <div className="flex items-start gap-3 mb-4">
                <input type="checkbox" id="dpdp-agree" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="consent-agreed-checkbox"
                       className="mt-1 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                <label htmlFor="dpdp-agree" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                  <strong>I, {consent.parent.full_name},</strong> being the {consent.parent.relationship} of {consent.student.full_name}, confirm I have
                  read the agreement above and grant <strong>verifiable parental consent</strong> for Deskey to process my child's personal data
                  for educational purposes under the DPDP Act 2023.
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={approve} disabled={approving || !verified || !agreed}
                        data-testid="consent-approve-btn"
                        className="flex-1 sm:flex-none h-12 px-7 text-sm font-bold text-white rounded-lg shadow-md hover:shadow-lg"
                        style={{ background: GREEN }}>
                  {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <CheckCircle2 className="w-4 h-4 mr-2"/>}
                  Yes, activate {consent.student.full_name.split(' ')[0]}'s account
                </Button>
                <Button onClick={reject} variant="outline" data-testid="consent-reject-btn"
                        className="h-12 px-5 text-sm text-slate-600 border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-700">
                  <XCircle className="w-4 h-4 mr-2"/> Decline
                </Button>
              </div>

              {!verified && <p className="text-[11px] text-amber-700 mt-3 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3"/> Verify your identity first using any method above.</p>}
            </div>

            {/* Footer */}
            <p className="text-[11px] text-slate-500 mt-6 text-center">
              Consent expires on {new Date(consent.expires_at).toLocaleString(undefined, { hour12: true })} · A copy of this approval will be e-mailed to {consent.parent.email}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function FactRow({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-[13px] font-semibold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}

function MockedBanner({ active, label, hint }) {
  if (!active) return null;
  return (
    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mb-3 flex items-center gap-1.5">
      <AlertTriangle className="w-3 h-3 flex-shrink-0"/> <strong>{label}</strong> — {hint}
    </div>
  );
}

function CenterLoader({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: SOFT }}>
      <div className="flex items-center gap-3 text-slate-500"><Loader2 className="w-5 h-5 animate-spin"/><span className="text-sm">{label}</span></div>
    </div>
  );
}
function ErrorCard({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: SOFT }}>
      <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-6 shadow text-center">
        <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2"/>
        <h3 className="text-base font-bold text-slate-900">Consent link unavailable</h3>
        <p className="text-sm text-slate-600 mt-1">{message}</p>
        <p className="text-[11px] text-slate-500 mt-3">If you received this link from your child's school, please contact them to re-issue it.</p>
      </div>
    </div>
  );
}
